import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useIssueAccessLink } from '../lib/queries';
import { Alert, Button, Card, SectionTitle } from './ui';

export function CustomerAccess({ customerId, otpAvailable }: { customerId: string; otpAvailable: boolean }) {
  const { t } = useI18n();
  const issue = useIssueAccessLink(customerId);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const link = issue.data?.url ?? null;

  useEffect(() => {
    if (!link) { setQrUrl(null); return; }
    let alive = true;
    void QRCode.toDataURL(link, {
      errorCorrectionLevel: 'M', margin: 1, width: 512,
      color: { dark: '#090a0c', light: '#f0f0f5' },
    }).then((url) => { if (alive) setQrUrl(url); });
    return () => { alive = false; };
  }, [link]);

  useEffect(() => {
    if (!issue.data) return;
    setSecondsLeft(issue.data.expiresInMinutes * 60);
    const timer = setInterval(() => setSecondsLeft((left) => Math.max(0, left - 1)), 1_000);
    return () => clearInterval(timer);
  }, [issue.data]);

  const errorText = useApiError();
  const error = issue.error instanceof ApiError ? issue.error : null;
  const expired = Boolean(issue.data) && secondsLeft === 0;

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Clipboard access can be refused; the link is on screen either way.
    }
  };

  return (
    <Card>
      <SectionTitle>{t('access.title')}</SectionTitle>

      {!link && (
        <>
          <p className="text-sm leading-relaxed text-ink-dim">
            {otpAvailable
              ? t('access.intro')
              : t('access.codesOff')}
          </p>
          {error && <div className="mt-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}
          <Button className="mt-3 w-full" loading={issue.isPending} onClick={() => issue.mutate()}>{t('access.issue')}</Button>
        </>
      )}

      {link && (
        <>
          <p className="text-sm text-ink-dim">{t('access.pointCamera')}</p>

          <div className={`mt-3 flex justify-center transition-opacity ${expired ? 'opacity-30' : ''}`}>
            <div className="rounded-2xl bg-ink p-3">
              {qrUrl
                ? <img src={qrUrl} alt={t('access.qrAlt')} className="h-44 w-44" />
                : <div className="h-44 w-44 animate-pulse rounded-lg bg-muted" />}
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-line bg-raised px-3 py-2">
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="tabular block truncate text-xs text-brand hover:underline"
            >
              {link}
            </a>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <span className={`tabular text-xs ${expired ? 'text-danger' : 'text-ink-faint'}`}>
              {expired
                ? t('access.expired')
                : t('access.validFor', {
                  time: `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`,
                })}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={copy}>
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
              <Button size="sm" loading={issue.isPending} onClick={() => issue.mutate()}>{t('access.newLink')}</Button>
            </div>
          </div>

          <p className="mt-3 border-t border-line pt-3 text-[0.7rem] leading-relaxed text-ink-faint">{t('access.singleUse')}</p>
        </>
      )}
    </Card>
  );
}
